import { useEffect, useState } from 'react';
import { useDebounce, useUpdateEffect } from 'react-use';
import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import moment from 'moment';
import * as chrono from 'chrono-node';

import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@treams/treams-icons';

import { Input } from './Input';
import { IconButton } from './IconButton';

const Header = styled(Box)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

const Toolbar = styled(Flex)``;

const MonthTitle = styled(Box)`
	font-size: 16px;
	font-weight: bold;
	text-align: center;
	user-select: none;
`;

const Week = styled(Flex)``;

const WeekNumber = styled(Box)`
	text-align: center;
	background: ${(props) => props.theme.colors.lightBlueLight};
	flex-basis: 0;
	flex-grow: 1;
	flex-shrink: 1;
	font-size: 12px;
	user-select: none;
`;

const DayNumberBadge = styled(Box)<{ $color: string }>`
	background: ${(props) => props.theme.colors[props.$color]};
	width: 24px;
	height: 24px;
	position: absolute;
	top: 50%;
	left: 50%;
	margin: -12px;
	border-radius: 12px;
`;

const DayNumberText = styled(Box)<{ badge?: boolean; included?: boolean }>`
	position: relative;
	color: ${(props) =>
		props.badge
			? props.theme.colors.white
			: props.included
			? 'black'
			: props.theme.colors.lightBlueDark};
`;

const DayNumber = styled(Box)<{ hoverable?: boolean }>`
	text-align: center;
	flex-basis: 0;
	flex-grow: 1;
	flex-shrink: 1;
	font-size: 12px;
	position: relative;
	background-color: transparent;
	border: 0;
	cursor: pointer;

	${(props) =>
		props.hoverable &&
		`&:hover {
		${DayNumberBadge} {
			background: ${props.theme.colors.lightBlueLight};
		}

		${DayNumberText} {
			color: ${props.theme.colors.antracite};
		}
	}`}
`;

function DatePickerDay(props: {
	start: moment.Moment;
	end: moment.Moment;
	date: moment.Moment;
	value?: moment.Moment;
	onClick?: () => void;
}) {
	const selected = props.value && props.date.isSame(props.value, 'day');
	const today = props.date.isSame(moment(), 'day');
	const included = props.date.isSameOrAfter(props.start) && props.date.isSameOrBefore(props.end);

	return (
		<DayNumber onClick={() => props.onClick?.()} hoverable={!selected && !today}>
			<Box my={2}>
				<DayNumberBadge $color={selected ? 'primary' : today ? 'slateBlue' : 'transparent'} />
				<DayNumberText badge={selected || today} included={included}>
					{props.date.date()}
				</DayNumberText>
			</Box>
		</DayNumber>
	);
}

function DatePickerWeek(props: {
	start: moment.Moment;
	end: moment.Moment;
	date: moment.Moment;
	value?: moment.Moment;
	last?: boolean;
	onChange: (date: moment.Moment) => void;
}) {
	return (
		<Week pr={2} alignItems="flex-start">
			<WeekNumber pl={2} py={2} pb={props.last ? 3 : 2}>
				{props.date.week()}
			</WeekNumber>
			{[0, 1, 2, 3, 4, 5, 6].map((day) => (
				<DatePickerDay
					key={day}
					start={props.start}
					end={props.end}
					date={props.date.clone().add(day, 'days')}
					value={props.value}
					onClick={() => {
						props.onChange(props.date.clone().add(day, 'days'));
					}}
				/>
			))}
		</Week>
	);
}

function DatePickerMonth(props: {
	date: moment.Moment;
	value?: moment.Moment;
	onChange: (date: moment.Moment) => void;
}) {
	const week = props.date.clone().startOf('month').startOf('week');
	const start = props.date.clone().startOf('month');
	const end = props.date.clone().endOf('month');

	const dates: moment.Moment[] = [];

	let last = week.clone();

	while (last.isSameOrBefore(end) || dates.length < 6) {
		dates.push(last);

		last = last.clone().add(1, 'week');
	}

	return (
		<>
			{dates.map((date, i) => (
				<DatePickerWeek
					key={i}
					start={start}
					end={end}
					date={date}
					value={props.value}
					onChange={props.onChange}
				/>
			))}
		</>
	);
}

export function DatePicker(props: {
	value?: Date;
	onChange: (date?: Date) => void;
	onDismiss: () => void;
}) {
	const [query, setQuery] = useState('');
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation();

	const [date, setDate] = useState<Date | undefined>(props.value);
	const [anchor, setAnchor] = useState<moment.Moment>(date ? moment(date) : moment());

	useUpdateEffect(() => {
		props.onChange(date);
	}, [date]);

	useEffect(() => {
		if (query === '') {
			return;
		}
		setLoading(true);
	}, [query]);

	useDebounce(
		() => {
			if (query === '') {
				return;
			}

			const date = chrono.nl.parseDate(query);

			if (date && date.valueOf()) {
				setDate(date);
				setAnchor(moment(date));
			}

			setLoading(false);
		},
		200,
		[query],
	);

	return (
		<Box width={260}>
			<Header p={2}>
				<Input
					small
					loading={loading}
					icon={<SearchIcon />}
					value={query}
					onChange={setQuery}
					autoFocus
					autoComplete="off"
					placeholder={t('datePicker.query.placeholder')}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							props.onDismiss();
						}
					}}
				/>
			</Header>
			<Toolbar p={3} alignItems="center">
				<IconButton
					type="secondary"
					icon={<ChevronLeftIcon />}
					small
					onClick={() => {
						setAnchor(moment(anchor).subtract('1', 'month'));
					}}
				/>
				<MonthTitle flexGrow={1} flexShrink={1}>
					{anchor.format('MMMM YYYY')}
				</MonthTitle>
				<IconButton
					type="secondary"
					icon={<ChevronRightIcon />}
					small
					onClick={() => {
						setAnchor(moment(anchor).add('1', 'month'));
					}}
				/>
			</Toolbar>
			<DatePickerMonth
				date={anchor}
				value={moment(date)}
				onChange={(newDate) => {
					setDate(newDate.toDate());
					props.onDismiss();
				}}
			/>
		</Box>
	);
}
